import { httpClient } from './httpClient';

const PREFIX = '/app/helper';

function readNum(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function readStr(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    const s = String(v).trim();
    if (s) return s;
  }
  return undefined;
}

function readStrOrNumberStatus(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (v == null) continue;
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
    if (typeof v === 'boolean') return v ? 'true' : 'false';
    const s = String(v).trim();
    if (s) return s;
  }
  return undefined;
}

function readPercent100(o: Record<string, unknown>, keys: string[]): number | undefined {
  const n = readNum(o, keys);
  if (n === undefined || !Number.isFinite(n)) return undefined;
  if (n >= 0 && n <= 1) return n * 100;
  return n;
}

function readBool(o: Record<string, unknown>, keys: string[]): boolean {
  for (const k of keys) {
    const v = o[k];
    if (v === true || v === 1 || v === '1' || v === 'true' || v === 'True') return true;
    if (v === false || v === 0 || v === '0' || v === 'false' || v === 'False') return false;
  }
  return false;
}

/** One expense line: English label (legacy MeheResource equivalent) + LL + USD from API. */
export interface BudgetLineAmountRow {
  name: string;
  ll: number;
  usd: number;
}

/** Budget summary table row (Criteria | LL | USD | Main | Notes) aligned with legacy modal. */
export interface BudgetSummaryGridRow {
  criteria: string;
  ll: number | null;
  usd: number | null;
  /** Main currency amount, or percent (0–100) when `mainIsPercent` is true. */
  main: number | null;
  mainIsPercent: boolean;
  note: string | null;
}

export interface BudgetCategoriesExpensesSummary {
  schoolName?: string;
  year: number;
  displayMainCurrency: 'LBP' | 'USD';
  totalExpensesMain?: number;
  totalRevenuesMain?: number;
  differenceMain?: number;
  abPercent?: number;
  abStatus?: string;
  cPercent?: number;
  cStatus?: string;
  totalAbMain?: number;
  totalAbcMain?: number;
  cSegmentMain?: number;
  totalDMain?: number;
  categoryA: BudgetLineAmountRow[];
  categoryB: BudgetLineAmountRow[];
  categoryC: BudgetLineAmountRow[];
  categoryD: BudgetLineAmountRow[];
  budgetSummary: BudgetSummaryGridRow[];
  /** Shown only when `isLastYearBudgetExist` (legacy `differenceTuitionRow`). */
  tuitionVsLastYear: BudgetSummaryGridRow | null;
}

function readRoot(raw: unknown): Record<string, unknown> {
  if (!raw || typeof raw !== 'object') return {};
  return raw as Record<string, unknown>;
}

function unwrapResult(raw: unknown): Record<string, unknown> {
  const root = readRoot(raw);
  const nested = readRoot(readRoot(root).result ?? readRoot(root).data);
  return Object.keys(nested).length ? nested : root;
}

function pushLine(rows: BudgetLineAmountRow[], name: string, ll?: number, usd?: number): void {
  rows.push({
    name,
    ll: ll !== undefined && Number.isFinite(ll) ? ll : 0,
    usd: usd !== undefined && Number.isFinite(usd) ? usd : 0,
  });
}

function readDynamicExpenseList(rawList: unknown): BudgetLineAmountRow[] {
  if (!Array.isArray(rawList)) return [];
  const out: BudgetLineAmountRow[] = [];
  for (const it of rawList) {
    if (!it || typeof it !== 'object') continue;
    const row = it as Record<string, unknown>;
    const name = readStr(row, ['name', 'Name']) ?? '—';
    const ll = readNum(row, ['amountLL', 'AmountLL']);
    const usd = readNum(row, ['amountUSD', 'AmountUSD']);
    if (name === '—') continue;
    out.push({
      name,
      ll: ll !== undefined && Number.isFinite(ll) ? ll : 0,
      usd: usd !== undefined && Number.isFinite(usd) ? usd : 0,
    });
  }
  return out;
}

function complianceNote(status: string | undefined): string {
  const s = String(status ?? '').trim();
  if (s === '0') return 'Fully compliant';
  if (s === '1') return 'Non-compliant';
  if (!s) return '—';
  return `Status ${s}`;
}

function buildCategoryA(o: Record<string, unknown>, year: number): BudgetLineAmountRow[] {
  const rows: BudgetLineAmountRow[] = [];
  pushLine(
    rows,
    'Basic salaries — permanent teaching staff',
    readNum(o, ['basicSalariesPermanentTeachingStaffLL', 'BasicSalariesPermanentTeachingStaffLL']),
    readNum(o, ['basicSalariesPermanentTeachingStaffUSD', 'BasicSalariesPermanentTeachingStaffUSD'])
  );
  pushLine(
    rows,
    'Basic salaries — contractual teaching staff',
    readNum(o, ['basicSalariesContractualTeachingStaffLL', 'BasicSalariesContractualTeachingStaffLL']),
    readNum(o, ['basicSalariesContractualTeachingStaffUSD', 'BasicSalariesContractualTeachingStaffUSD'])
  );
  pushLine(
    rows,
    'Overtime — permanent teaching staff',
    readNum(o, ['overtimeHoursPermanentTeachingStaffLL', 'OvertimeHoursPermanentTeachingStaffLL']),
    readNum(o, ['overtimeHoursPermanentTeachingStaffUSD', 'OvertimeHoursPermanentTeachingStaffUSD'])
  );
  pushLine(
    rows,
    'Bonuses & rewards — teaching staff (max 15%)',
    readNum(o, ['bonusesRewardsTeachingStaffMax15LL', 'BonusesRewardsTeachingStaffMax15LL']),
    readNum(o, ['bonusesRewardsTeachingStaffMax15USD', 'BonusesRewardsTeachingStaffMax15USD'])
  );
  pushLine(
    rows,
    'Basic salaries — administrative teaching staff',
    readNum(o, ['basicSalariesAdminstrativeTeachingStaffLL', 'BasicSalariesAdminstrativeTeachingStaffLL']),
    readNum(o, ['basicSalariesAdminstrativeTeachingStaffUSD', 'BasicSalariesAdminstrativeTeachingStaffUSD'])
  );
  pushLine(rows, 'Retro effect', readNum(o, ['retroEffectLL', 'RetroEffectLL']), readNum(o, ['retroEffectUSD', 'RetroEffectUSD']));
  if (year >= 2025) {
    pushLine(
      rows,
      'Social assistance — permanent teachers (category A)',
      readNum(o, ['socialAssistancePermanentTeachersLL', 'SocialAssistancePermanentTeachersLL']),
      readNum(o, ['socialAssistancePermanentTeachersUSD', 'SocialAssistancePermanentTeachersUSD'])
    );
    pushLine(
      rows,
      'Social assistance — contractual (category A)',
      readNum(o, ['socialAssistanceContractualLL', 'SocialAssistanceContractualLL']),
      readNum(o, ['socialAssistanceContractualUSD', 'SocialAssistanceContractualUSD'])
    );
    pushLine(
      rows,
      'Social assistance — administrative teachers (category A)',
      readNum(o, ['socialAssistanceAdministrativeTeachersLL', 'SocialAssistanceAdministrativeTeachersLL']),
      readNum(o, ['socialAssistanceAdministrativeTeachersUSD', 'SocialAssistanceAdministrativeTeachersUSD'])
    );
  }
  return rows;
}

function buildCategoryBFixed(o: Record<string, unknown>): BudgetLineAmountRow[] {
  const rows: BudgetLineAmountRow[] = [];
  pushLine(
    rows,
    'Transportation costs',
    readNum(o, ['transportationCostsLL', 'TransportationCostsLL']),
    readNum(o, ['transportationCostsUSD', 'TransportationCostsUSD'])
  );
  pushLine(
    rows,
    'Family compensation',
    readNum(o, ['familyCompensationLL', 'FamilyCompensationLL']),
    readNum(o, ['familyCompensationUSD', 'FamilyCompensationUSD'])
  );
  pushLine(
    rows,
    'NSSF contributions',
    readNum(o, ['contributionsNSSFLL', 'ContributionsNSSFLL']),
    readNum(o, ['contributionsNSSFUSD', 'ContributionsNSSFUSD'])
  );
  pushLine(
    rows,
    'Teachers compensation fund',
    readNum(o, ['contributionsFundLL', 'ContributionsFundLL']),
    readNum(o, ['contributionsFundUSD', 'ContributionsFundUSD'])
  );
  return rows;
}

function buildCategoryDFixed(o: Record<string, unknown>, year: number): BudgetLineAmountRow[] {
  const rows: BudgetLineAmountRow[] = [];
  pushLine(
    rows,
    'Bonuses and rewards (category D)',
    readNum(o, ['bonusesAndRewardsLL', 'BonusesAndRewardsLL']),
    readNum(o, ['bonusesAndRewardsUSD', 'BonusesAndRewardsUSD'])
  );
  pushLine(
    rows,
    'Scholarships — contractual children',
    readNum(o, ['scholarshipsForContractualChildrenLL', 'ScholarshipsForContractualChildrenLL']),
    readNum(o, ['scholarshipsForContractualChildrenUSD', 'ScholarshipsForContractualChildrenUSD'])
  );
  pushLine(
    rows,
    'Scholarships — administrative children',
    readNum(o, ['scholarshipsForAdministrativeChildrenLL', 'ScholarshipsForAdministrativeChildrenLL']),
    readNum(o, ['scholarshipsForAdministrativeChildrenUSD', 'ScholarshipsForAdministrativeChildrenUSD'])
  );
  pushLine(
    rows,
    'School scholarships — administrative',
    readNum(o, ['schoolScholarshipsForAdministrativeLL', 'SchoolScholarshipsForAdministrativeLL']),
    readNum(o, ['schoolScholarshipsForAdministrativeUSD', 'SchoolScholarshipsForAdministrativeUSD'])
  );
  pushLine(
    rows,
    'Severance — permanent staff',
    readNum(o, ['severanceCompensationsForPermanentLL', 'SeveranceCompensationsForPermanentLL']),
    readNum(o, ['severanceCompensationsForPermanentUSD', 'SeveranceCompensationsForPermanentUSD'])
  );
  if (year < 2025) {
    pushLine(
      rows,
      'Social assistance — permanent teachers (category D)',
      readNum(o, ['socialAssistancePermanentTeachersLL', 'SocialAssistancePermanentTeachersLL']),
      readNum(o, ['socialAssistancePermanentTeachersUSD', 'SocialAssistancePermanentTeachersUSD'])
    );
    pushLine(
      rows,
      'Social assistance — contractual (category D)',
      readNum(o, ['socialAssistanceContractualLL', 'SocialAssistanceContractualLL']),
      readNum(o, ['socialAssistanceContractualUSD', 'SocialAssistanceContractualUSD'])
    );
    pushLine(
      rows,
      'Social assistance — administrative teachers (category D)',
      readNum(o, ['socialAssistanceAdministrativeTeachersLL', 'SocialAssistanceAdministrativeTeachersLL']),
      readNum(o, ['socialAssistanceAdministrativeTeachersUSD', 'SocialAssistanceAdministrativeTeachersUSD'])
    );
  }
  const addTasks = readNum(o, [
    'additionalTasksFeesForAdminstrative',
    'AdditionalTasksFeesForAdminstrative',
    'additionalTasksFeesForAdministrative',
    'AdditionalTasksFeesForAdministrative',
  ]);
  pushLine(rows, 'Additional tasks / fees — administrative', addTasks ?? 0, 0);
  return rows;
}

function sumExpenseListAmountLL(list: unknown): number {
  if (!Array.isArray(list)) return 0;
  let s = 0;
  for (const it of list) {
    if (!it || typeof it !== 'object') continue;
    const row = it as Record<string, unknown>;
    const ll = readNum(row, ['amountLL', 'AmountLL']);
    if (ll !== undefined && Number.isFinite(ll)) s += ll;
  }
  return s;
}

export function mapBudgetCategoriesExpenses(raw: unknown): BudgetCategoriesExpensesSummary {
  const o = unwrapResult(raw);
  const schoolName = readStr(o, [
    'schoolName',
    'SchoolName',
    'budgetSchoolName',
    'BudgetSchoolName',
    'school',
    'School',
    'arabicSchoolName',
    'ArabicSchoolName',
  ]);
  const yearNum = readNum(o, ['year', 'Year', 'academicYear', 'AcademicYear', 'budgetYear', 'BudgetYear']);
  const year = yearNum !== undefined && Number.isFinite(yearNum) ? Math.round(yearNum) : 0;

  const totalExpensesMain = readNum(o, [
    'totalExpensesMainCurrencey',
    'TotalExpensesMainCurrencey',
    'totalExpensesMainCurrency',
    'TotalExpensesMainCurrency',
  ]);
  const totalExpensesUSD = readNum(o, ['totalExpensesUSD', 'TotalExpensesUSD']);
  const totalRevenuesMain = readNum(o, [
    'totalRevenuesMainCurrencey',
    'TotalRevenuesMainCurrencey',
    'totalRevenuesMainCurrency',
    'TotalRevenuesMainCurrency',
  ]);
  const totalRevenuesLL = readNum(o, ['totalRevenuesLL', 'TotalRevenuesLL']);
  const totalRevenuesUSD = readNum(o, ['totalRevenuesUSD', 'TotalRevenuesUSD']);
  const totalExpensesLL = readNum(o, ['totalExpensesLL', 'TotalExpensesLL']);
  const differenceMain = readNum(o, [
    'differenceBetweenExpensesandRevenuesMainCurrencey',
    'DifferenceBetweenExpensesandRevenuesMainCurrencey',
    'differenceBetweenExpensesAndRevenuesMainCurrencey',
    'DifferenceBetweenExpensesAndRevenuesMainCurrencey',
    'differenceMainCurrencey',
    'DifferenceMainCurrencey',
  ]);
  const differenceLL = readNum(o, [
    'differenceBetweenExpensesandRevenuesLL',
    'DifferenceBetweenExpensesandRevenuesLL',
  ]);
  const differenceUSD = readNum(o, [
    'differenceBetweenExpensesandRevenuesUSD',
    'DifferenceBetweenExpensesandRevenuesUSD',
  ]);
  const differenceStatus = readStrOrNumberStatus(o, [
    'differenceBetweenExpensesandRevenuesStatus',
    'DifferenceBetweenExpensesandRevenuesStatus',
  ]);
  const costPerStudentUSD = readNum(o, ['costPerStudentUSD', 'CostPerStudentUSD']);
  const costPerStudentLL = readNum(o, ['costPerStudentLL', 'CostPerStudentLL']);
  const costPerStudentMain = readNum(o, ['costPerStudentMainCurrencey', 'CostPerStudentMainCurrencey']);
  const totalAbLL = readNum(o, ['totalCategoryAandBLL', 'TotalCategoryAandBLL']);
  const totalAbUSD = readNum(o, ['totalCategoryAandBUSD', 'TotalCategoryAandBUSD']);
  const totalAbMain = readNum(o, [
    'totalCategoryAandBMainCurrencey',
    'TotalCategoryAandBMainCurrencey',
    'totalCategoryAAndBMainCurrencey',
    'TotalCategoryAAndBMainCurrencey',
  ]);
  const totalAbcLL = readNum(o, ['totalCategoryAandBandCLL', 'TotalCategoryAandBandCLL']);
  const totalAbcUSD = readNum(o, ['totalCategoryAandBandCUSD', 'TotalCategoryAandBandCUSD']);
  const totalAbcMain = readNum(o, [
    'totalCategoryAandBandCMainCurrencey',
    'TotalCategoryAandBandCMainCurrencey',
    'totalCategoryAAndBAndCMainCurrencey',
    'TotalCategoryAAndBAndCMainCurrencey',
  ]);
  const abPercent = readPercent100(o, [
    'abOutOfABCTotaltMainCurrencey',
    'ABOutOfABCTotaltMainCurrencey',
    'abOutOfAbcTotalMainCurrencey',
    'ABOutOfAbcTotalMainCurrencey',
    'abOutOfAbcPercent',
    'ABOutOfAbcPercent',
  ]);
  const cPercent = readPercent100(o, [
    'cOutOfABCTotaltMainCurrencey',
    'COutOfABCTotaltMainCurrencey',
    'cOutOfAbcTotalMainCurrencey',
    'COutOfAbcTotalMainCurrencey',
    'cOutOfAbcPercent',
    'COutOfAbcPercent',
  ]);
  const abStatus = readStrOrNumberStatus(o, [
    'abOutOfABCStatus',
    'ABOutOfABCStatus',
    'abStatus',
    'ABStatus',
    'aBComplianceStatus',
    'ABComplianceStatus',
  ]);
  const cStatus = readStrOrNumberStatus(o, [
    'cOutOfABCStatus',
    'COutOfABCStatus',
    'cStatus',
    'CStatus',
    'cComplianceStatus',
    'CComplianceStatus',
  ]);
  let totalDMain = readNum(o, [
    'totalCategoryDMainCurrencey',
    'TotalCategoryDMainCurrencey',
    'totalCategoryDLL',
    'TotalCategoryDLL',
    'budgetSummary_DTotalMainCurruncy',
    'BudgetSummary_DTotalMainCurruncy',
    'budgetSummaryDTotalMainCurruncy',
    'BudgetSummaryDTotalMainCurruncy',
  ]);
  const rawDList =
    (o.budgetCategoryDExpnses as unknown) ??
    (o.BudgetCategoryDExpnses as unknown) ??
    (o.budgetCategoryDExpenses as unknown) ??
    (o.BudgetCategoryDExpenses as unknown);
  if ((totalDMain === undefined || totalDMain === 0) && rawDList) {
    const summed = sumExpenseListAmountLL(rawDList);
    if (summed > 0) totalDMain = summed;
  }
  let cSegmentMain: number | undefined;
  if (totalAbMain !== undefined && totalAbcMain !== undefined && totalAbcMain >= totalAbMain) {
    cSegmentMain = totalAbcMain - totalAbMain;
  }
  const displayMainCurrency: 'LBP' | 'USD' =
    totalExpensesUSD != null && totalExpensesUSD > 0 ? 'USD' : 'LBP';

  const rawB =
    (o.budgetCategoryBExpnses as unknown) ??
    (o.BudgetCategoryBExpnses as unknown) ??
    (o.budgetCategoryBExpenses as unknown) ??
    (o.BudgetCategoryBExpenses as unknown);
  const rawC =
    (o.budgetCategoryCExpnses as unknown) ??
    (o.BudgetCategoryCExpnses as unknown) ??
    (o.budgetCategoryCExpenses as unknown) ??
    (o.BudgetCategoryCExpenses as unknown);

  const categoryA = buildCategoryA(o, year);
  const categoryB = [...buildCategoryBFixed(o), ...readDynamicExpenseList(rawB)];
  const categoryC = readDynamicExpenseList(rawC);
  const categoryD = [...buildCategoryDFixed(o, year), ...readDynamicExpenseList(rawDList)];

  const budgetSummary: BudgetSummaryGridRow[] = [
    {
      criteria: 'Total (A + B)',
      ll: totalAbLL ?? null,
      usd: totalAbUSD ?? null,
      main: totalAbMain ?? null,
      mainIsPercent: false,
      note: null,
    },
    {
      criteria: 'Total (A + B + C)',
      ll: totalAbcLL ?? null,
      usd: totalAbcUSD ?? null,
      main: totalAbcMain ?? null,
      mainIsPercent: false,
      note: null,
    },
    {
      criteria: 'A+B as % of (A+B+C)',
      ll: null,
      usd: null,
      main: abPercent ?? null,
      mainIsPercent: true,
      note: complianceNote(abStatus),
    },
    {
      criteria: 'C as % of (A+B+C)',
      ll: null,
      usd: null,
      main: cPercent ?? null,
      mainIsPercent: true,
      note: complianceNote(cStatus),
    },
    {
      criteria: 'Total expenses',
      ll: totalExpensesLL ?? null,
      usd: totalExpensesUSD ?? null,
      main: totalExpensesMain ?? null,
      mainIsPercent: false,
      note: null,
    },
    {
      criteria: 'Total revenues',
      ll: totalRevenuesLL ?? null,
      usd: totalRevenuesUSD ?? null,
      main: totalRevenuesMain ?? null,
      mainIsPercent: false,
      note: null,
    },
    {
      criteria: 'Expenses − revenues',
      ll: differenceLL ?? null,
      usd: differenceUSD ?? null,
      main: differenceMain ?? null,
      mainIsPercent: false,
      note: complianceNote(differenceStatus),
    },
    {
      criteria: 'Cost per student',
      ll: costPerStudentLL ?? null,
      usd: costPerStudentUSD ?? null,
      main: costPerStudentMain ?? null,
      mainIsPercent: false,
      note: null,
    },
  ];

  const isLastYear = readBool(o, ['isLastYearBudgetExist', 'IsLastYearBudgetExist']);
  const diffTuitionLL = readNum(o, ['diffrenceAvgTutionLL', 'DiffrenceAvgTutionLL', 'differenceAvgTuitionLL']);
  const diffTuitionUSD = readNum(o, ['diffrenceAvgTutionUSD', 'DiffrenceAvgTutionUSD', 'differenceAvgTuitionUSD']);
  const diffTuitionMain = readNum(o, [
    'diffrenceAvgTutionMainCurrency',
    'DiffrenceAvgTutionMainCurrency',
    'differenceAvgTuitionMainCurrency',
  ]);
  const tuitionVsLastYear: BudgetSummaryGridRow | null = isLastYear
    ? {
        criteria: 'Tuition vs last year (avg. difference)',
        ll: diffTuitionLL ?? null,
        usd: diffTuitionUSD ?? null,
        main: diffTuitionMain ?? null,
        mainIsPercent: false,
        note: null,
      }
    : null;

  return {
    schoolName,
    year,
    displayMainCurrency,
    totalExpensesMain,
    totalRevenuesMain,
    differenceMain,
    abPercent,
    abStatus,
    cPercent,
    cStatus,
    totalAbMain,
    totalAbcMain,
    cSegmentMain,
    totalDMain,
    categoryA,
    categoryB,
    categoryC,
    categoryD,
    budgetSummary,
    tuitionVsLastYear,
  };
}

export async function fetchBudgetCategoriesExpenses(budgetId: string): Promise<BudgetCategoriesExpensesSummary> {
  const raw = await httpClient.get<unknown>(
    `${PREFIX}/budget-categories-expenses/${encodeURIComponent(budgetId)}`
  );
  return mapBudgetCategoriesExpenses(raw);
}
